export type Unit = 'ppm' | '%' | 'g/t';

export interface MethodElementLimit {
  element: string;
  limit: number;
  unit: Unit;
  limitInPercent?: number;
}

export type MethodCode = 'D4A-ICP- PRE.MO/ANA/038' | 'FP-ICP PRE.MO/ANA/007' | 'BGM-MULTI-LAB' | string;

// ICP 4 acides limits (from provided table)
const ICP4A: Record<string, MethodElementLimit> = {
  // Majors as %
  Al: { element: 'Al', limit: 0.01, unit: '%' },
  Ca: { element: 'Ca', limit: 0.01, unit: '%' },
  Fe: { element: 'Fe', limit: 0.01, unit: '%' },
  K: { element: 'K', limit: 0.01, unit: '%' },
  Mg: { element: 'Mg', limit: 0.01, unit: '%' },
  Mn: { element: 'Mn', limit: 0.01, unit: '%' },
  Na: { element: 'Na', limit: 0.01, unit: '%' },
  P: { element: 'P', limit: 0.01, unit: '%' },
  S: { element: 'S', limit: 0.01, unit: '%' },
  Si: { element: 'Si', limit: 0.01, unit: '%' },
  Ti: { element: 'Ti', limit: 0.01, unit: '%' },
  // Traces ppm
  Ag: { element: 'Ag', limit: 1, unit: 'ppm' },
  As: { element: 'As', limit: 8, unit: 'ppm' },
  B: { element: 'B', limit: 5, unit: 'ppm' },
  Ba: { element: 'Ba', limit: 2, unit: 'ppm' },
  Be: { element: 'Be', limit: 1, unit: 'ppm' },
  Bi: { element: 'Bi', limit: 10, unit: 'ppm' },
  Cd: { element: 'Cd', limit: 1, unit: 'ppm' },
  Co: { element: 'Co', limit: 5, unit: 'ppm' },
  Cr: { element: 'Cr', limit: 5, unit: 'ppm' },
  Cu: { element: 'Cu', limit: 2, unit: 'ppm', limitInPercent: 0.01 }, // fix 2026-06-19: ANA/038 Cu LOD is 2 ppm (matches backend method-detection-limits + cert)
  Ga: { element: 'Ga', limit: 5, unit: 'ppm' },
  Ge: { element: 'Ge', limit: 10, unit: 'ppm' },
  In: { element: 'In', limit: 5, unit: 'ppm' },
  Li: { element: 'Li', limit: 2, unit: 'ppm' },
  Mo: { element: 'Mo', limit: 2, unit: 'ppm' },
  Nb: { element: 'Nb', limit: 1, unit: 'ppm' },
  Ni: { element: 'Ni', limit: 2, unit: 'ppm' },
  Pb: { element: 'Pb', limit: 2, unit: 'ppm', limitInPercent: 0.01 },
  Sb: { element: 'Sb', limit: 1, unit: 'ppm' },
  Se: { element: 'Se', limit: 2, unit: 'ppm' },
  Sn: { element: 'Sn', limit: 2, unit: 'ppm' },
  Sr: { element: 'Sr', limit: 2, unit: 'ppm' },
  Ta: { element: 'Ta', limit: 1, unit: 'ppm' },
  Th: { element: 'Th', limit: 5, unit: 'ppm' },
  Tl: { element: 'Tl', limit: 1, unit: 'ppm' },
  V: { element: 'V', limit: 1, unit: 'ppm' },
  W: { element: 'W', limit: 2, unit: 'ppm' },
  Y: { element: 'Y', limit: 1, unit: 'ppm' },
  Zn: { element: 'Zn', limit: 2, unit: 'ppm', limitInPercent: 0.01 },
  Zr: { element: 'Zr', limit: 1, unit: 'ppm' },
};

// Rare earths (ppm)
const RARE_EARTHS: Record<string, MethodElementLimit> = {
  La: { element: 'La', limit: 1, unit: 'ppm' },
  Ce: { element: 'Ce', limit: 1, unit: 'ppm' },
  Pr: { element: 'Pr', limit: 5, unit: 'ppm' },
  Nd: { element: 'Nd', limit: 5, unit: 'ppm' },
  Sm: { element: 'Sm', limit: 5, unit: 'ppm' },
  Eu: { element: 'Eu', limit: 5, unit: 'ppm' },
  Gd: { element: 'Gd', limit: 5, unit: 'ppm' },
  Tb: { element: 'Tb', limit: 5, unit: 'ppm' },
  Dy: { element: 'Dy', limit: 5, unit: 'ppm' },
  Ho: { element: 'Ho', limit: 5, unit: 'ppm' },
  Er: { element: 'Er', limit: 5, unit: 'ppm' },
  Tm: { element: 'Tm', limit: 5, unit: 'ppm' },
  Yb: { element: 'Yb', limit: 5, unit: 'ppm' },
  Lu: { element: 'Lu', limit: 5, unit: 'ppm' },
  Sc: { element: 'Sc', limit: 5, unit: 'ppm' },
  U: { element: 'U', limit: 5, unit: 'ppm' },
};

// Fusion limits (subset from provided table)
const ICPFUSION: Record<string, MethodElementLimit> = {
  Al: { element: 'Al', limit: 0.01, unit: '%' },
  Ca: { element: 'Ca', limit: 0.01, unit: '%' },
  Fe: { element: 'Fe', limit: 0.01, unit: '%' },
  K: { element: 'K', limit: 0.01, unit: '%' },
  Mg: { element: 'Mg', limit: 0.01, unit: '%' },
  Mn: { element: 'Mn', limit: 0.01, unit: '%' },
  Na: { element: 'Na', limit: 0.01, unit: '%' },
  P: { element: 'P', limit: 0.01, unit: '%' },
  S: { element: 'S', limit: 0.01, unit: '%' },
  Si: { element: 'Si', limit: 0.01, unit: '%' },
  Ti: { element: 'Ti', limit: 0.01, unit: '%' },
  Al2O3: { element: 'Al2O3', limit: 0.01, unit: '%' },
  CaO: { element: 'CaO', limit: 0.01, unit: '%' },
  Fe2O3: { element: 'Fe2O3', limit: 0.01, unit: '%' },
  K2O: { element: 'K2O', limit: 0.01, unit: '%' },
  MgO: { element: 'MgO', limit: 0.01, unit: '%' },
  MnO: { element: 'MnO', limit: 0.01, unit: '%' },
  P2O5: { element: 'P2O5', limit: 0.01, unit: '%' },
  SiO2: { element: 'SiO2', limit: 0.01, unit: '%' },
  TiO2: { element: 'TiO2', limit: 0.01, unit: '%' },
  As: { element: 'As', limit: 8, unit: 'ppm' },
  Ba: { element: 'Ba', limit: 2, unit: 'ppm' },
  Be: { element: 'Be', limit: 1, unit: 'ppm' },
  Bi: { element: 'Bi', limit: 10, unit: 'ppm' },
  Cd: { element: 'Cd', limit: 1, unit: 'ppm' },
  Co: { element: 'Co', limit: 5, unit: 'ppm' },
  Cr: { element: 'Cr', limit: 5, unit: 'ppm' },
  Cu: { element: 'Cu', limit: 2, unit: 'ppm' },
  Li: { element: 'Li', limit: 20, unit: 'ppm' },
  Mo: { element: 'Mo', limit: 10, unit: 'ppm' },
  Ni: { element: 'Ni', limit: 20, unit: 'ppm' },
  Pb: { element: 'Pb', limit: 26, unit: 'ppm' },
  Sb: { element: 'Sb', limit: 1, unit: 'ppm' },
  Se: { element: 'Se', limit: 30, unit: 'ppm' },
  Sn: { element: 'Sn', limit: 20, unit: 'ppm' },
  Sr: { element: 'Sr', limit: 2, unit: 'ppm' },
  Ta: { element: 'Ta', limit: 1, unit: 'ppm' },
  Tl: { element: 'Tl', limit: 1, unit: 'ppm' },
  V: { element: 'V', limit: 1, unit: 'ppm' },
  Y: { element: 'Y', limit: 1, unit: 'ppm' },
  Zn: { element: 'Zn', limit: 2, unit: 'ppm' },
};

export const METHOD_LIMITS: Record<MethodCode, Record<string, MethodElementLimit>> = {
  'D4A-ICP- PRE.MO/ANA/038': { ...ICP4A, ...RARE_EARTHS }, // 41 or 55 with rares
  'FP-ICP PRE.MO/ANA/007': ICPFUSION,
  'PRE.MO/ANA/007': ICPFUSION,
  'BGM-MULTI-LAB': {
    Ag: { element: 'Ag', limit: 1, unit: 'g/t' },
    As: { element: 'As', limit: 1, unit: 'ppm' },
    Mo: { element: 'Mo', limit: 1, unit: 'ppm' },
    Sn: { element: 'Sn', limit: 1, unit: 'ppm' },
    Cu: { element: 'Cu', limit: 0.01, unit: '%' },
    Fe: { element: 'Fe', limit: 0.01, unit: '%' },
    Pb: { element: 'Pb', limit: 0.01, unit: '%' },
    Zn: { element: 'Zn', limit: 0.01, unit: '%' },
    Au: { element: 'Au', limit: 0.05, unit: 'g/t' },
  },
};

export function getMethodDetectionLimit(methodCode: MethodCode | undefined, element: string): MethodElementLimit | undefined {
  if (!methodCode) return undefined;
  let table = METHOD_LIMITS[methodCode];
  if (!table) {
    // Normalize method-code variants to canonical keys. BGM is intentionally left untouched.
    if (!/BGM/i.test(methodCode)) {
      if (/038|037|041/.test(methodCode)) table = METHOD_LIMITS['D4A-ICP- PRE.MO/ANA/038'];
      else if (/007/.test(methodCode)) table = METHOD_LIMITS['FP-ICP PRE.MO/ANA/007'];
    }
  }
  if (!table) return undefined;
  const OXIDE_TO_SYMBOL: Record<string, string> = { Al2O3: 'Al', CaO: 'Ca', Fe2O3: 'Fe', K2O: 'K', MgO: 'Mg', MnO: 'Mn', Na2O: 'Na', P2O5: 'P', SiO2: 'Si', TiO2: 'Ti', Cr2O3: 'Cr', V2O5: 'V', BaO: 'Ba', SrO: 'Sr', SO3: 'S' };
  return table[element] || table[element?.trim()] || table[element?.toUpperCase()] || (element && OXIDE_TO_SYMBOL[element] ? table[OXIDE_TO_SYMBOL[element]] : undefined);
}

/**
 * Convert internal ICP storage to display value for method unit.
 * Majors in % are stored as ppm×10000 in `concentration_ppm` (e.g. 97400 → 9.74 %).
 * Legacy rows sometimes store the literal percent in the same field when the value is below 100.
 */
export function shouldUseTracePercentColumn(limit: MethodElementLimit | undefined, maxStoredPpm: number): boolean {
  return !!limit && limit.unit === 'ppm' && Number.isFinite(maxStoredPpm) && maxStoredPpm > 10000;
}

export function icpStoredPpmToDisplayValue(limit: MethodElementLimit | undefined, value: number, columnForcePercent?: boolean): number {
  if (!limit || !Number.isFinite(value)) return value;
  if (columnForcePercent && limit.unit === 'ppm') return value / 10000;
  if (limit.unit === '%') {
    return value > 100 ? value / 10000 : value;
  }
  return value;
}

export function formatWithMethodDetectionLimitFrontend(
  methodCode: MethodCode | undefined,
  element: string,
  value: number,
  options?: { columnForcePercent?: boolean; finalPercent?: number | null },
): string {
  const limit = getMethodDetectionLimit(methodCode, element);
  if (!limit) {
    return Number.isFinite(value) ? value.toString() : '0';
  }

  // Method reports in % — compare and format in percent space
  if (limit.unit === '%') {
    const pct = icpStoredPpmToDisplayValue(limit, value, false, options?.finalPercent);
    if (pct <= 0) return `<${limit.limit}`;
    if (pct < limit.limit) return `<${limit.limit}`;
    return pct.toFixed(2);
  }

  const columnForcePercent = options?.columnForcePercent === true;
  if (limit.unit === 'ppm' && columnForcePercent) {
    const pct = value / 10000;
    const lod = limit.limitInPercent ?? 0.01;
    if (value <= 0) return `<${lod}`;
    if (pct < lod) return `<${lod}`;
    return pct.toFixed(2);
  }

  if (value <= 0) return `<${limit.limit}`;
  if (value < limit.limit) return `<${limit.limit}`;

  // Golden rule: ppm > 10000 => % (per-cell; validation table uses columnForcePercent instead)
  let displayValue = value;
  let displayUnit: Unit = limit.unit;
  let displayLod = limit.limit;
  if (displayUnit === 'ppm' && value > 10000) {
    displayValue = value / 10000;
    displayUnit = '%';
    displayLod = limit.limitInPercent ?? 0.01;
    if (displayValue < displayLod) return `<${displayLod}`;
  }

  if (displayUnit === '%') return displayValue.toFixed(2);
  if (displayUnit === 'g/t') return displayValue.toFixed(2);
  return displayValue.toFixed(3);
}


